/**
 * MemoForm's title row (notes and tasks): title `<input>` + the privacy /
 * owner cluster + (optional) close button. The cluster arrives as the
 * `controls` slot so this row never has to know what's in it; the task-only
 * date and stage controls live in TaskFormMetadataBar below, not here.
 */

import { XClose } from '@untitledui/icons';
import type { KeyboardEvent, ReactNode, Ref } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { Input } from '@/lib/base/forms/input/input';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

import { MEMO_ICON_BUTTON_CLASS } from './memoIconButton';

export interface MemoFormTitleRowProps {
  ref?: Ref<HTMLInputElement>;
  /**
   * Render the title input. Tasks always do; notes only when a legacy note
   * already carries a real title. When false the row keeps its `controls`
   * right-aligned at the same 24px height. (MemoForm renders this row at all
   * only when `!isInline || showTitleInput`, so a title-less inline editor
   * has no row — its controls sit on the attribution row below the body.)
   */
  showTitleInput: boolean;
  /**
   * The privacy + owner cluster, right-aligned beside the title. Omitted by
   * the inline (record-slideout) variant, which renders the same cluster at
   * the end of the attribution row below the body instead — in read and edit
   * alike — so the body's top edge is identical in both.
   */
  controls?: ReactNode;
  title: string;
  /** react-aria's Input reports the raw string, not a ChangeEvent. */
  onTitleChange: (value: string) => void;
  onKeyDown: (e: KeyboardEvent) => void;
  submitLock: boolean;
  titlePlaceholder: string;
  mode: 'create' | 'edit';
  isInline: boolean;
  size: 'default' | 'lg';
  showCloseButton: boolean;
  onCancel: () => void;
}

export function MemoFormTitleRow({
  ref,
  showTitleInput,
  controls,
  title,
  onTitleChange,
  onKeyDown,
  submitLock,
  titlePlaceholder,
  mode,
  isInline,
  size,
  showCloseButton,
  onCancel,
}: MemoFormTitleRowProps) {
  return (
    <div
      className={cx(
        'flex items-center gap-1',
        // Inline: flush horizontally (NoteCard's box provides the padding),
        // NO bottom padding, and pinned to the read view's 24px title row
        // (`min-h-6` — NoteCard's read <h4> uses the same) so the body below
        // lands at the SAME y in read and edit: no vertical jump on click-in.
        // Elsewhere the size-6 controls set the row height.
        isInline ? 'min-h-6' : 'px-3 pt-1.5 pb-0.5'
      )}
    >
      {/* No title input (a note): a bare spacer keeps the controls pinned
          right, in both create and edit — the composer never renders a
          placeholder title. MemoForm skips this row entirely for a
          title-less inline editor, so reaching here always means a
          standalone (non-inline) composer. */}
      {!showTitleInput && <div className='flex-1' />}
      {/* @/lib/base `Input` instead of a raw <input>, fully re-skinned via
          wrapperClassName/inputClassName: the base field chrome (rounded
          ring/shadow/bg, focus ring, size-scale padding+type) is stripped
          and replaced 1:1 with the borderless inline-title style below —
          the base Input's own `sm:` breakpoint on its `xs` size scale is
          explicitly re-neutralized at each step so no size-scale value
          leaks through at an unintended breakpoint. */}
      {showTitleInput && (
        <Input
          ref={ref}
          size='xs'
          value={title}
          onChange={onTitleChange}
          onKeyDown={onKeyDown}
          isDisabled={submitLock}
          placeholder={titlePlaceholder}
          aria-label={
            mode === 'create'
              ? `New ${titlePlaceholder.toLowerCase()}`
              : `Edit ${titlePlaceholder.toLowerCase()}`
          }
          // `flex-1 min-w-0` and `self-start` both go on the OUTER TextField
          // (className), not the inner group (wrapperClassName): the outer field
          // is the flex item in this row. `flex-1 min-w-0` there lets the title
          // shrink so the privacy/owner cluster and close button always fit
          // (on the inner group it did nothing — the group isn't the row's flex
          // child, so the field stayed at content width and pushed the controls
          // out on narrow standalone composers). `self-start` top-aligns the
          // title; on the inner wrapper it left the 20px title centered ~2px
          // below read mode.
          className={cx('flex-1 min-w-0', isInline && 'self-start')}
          // Borderless at rest to pixel-match the read <h4>, but restore the
          // base field's brand focus ring on focus so keyboard users can see
          // the active field (`ring-0` alone suppressed the group's
          // focus-within ring while the input's `outline-hidden` removed the
          // native outline). The ring is inset (base group sets `ring-inset`)
          // and doesn't affect layout, so the no-vertical-jump design holds.
          wrapperClassName={cx(
            'min-w-0 rounded-none bg-transparent shadow-none ring-0 focus-within:ring-2 focus-within:ring-brand'
          )}
          inputClassName={cx(
            'font-semibold text-fg-primary placeholder:text-fg-quaternary',
            // Inline: pixel-match the read <h4> (`text-sm font-semibold`,
            // 20px line-height, zero padding). `self-start` (above, on the
            // outer field) top-aligns it in the flex row — its size-6
            // owner/privacy buttons make the row 24px, and items-center would
            // otherwise drop the 20px title 2px below where the read <h4>
            // sits. The icons stay centered.
            isInline && 'text-sm leading-5 p-0 sm:text-sm',
            !isInline &&
              size === 'lg' &&
              'text-base sm:text-base md:text-lg p-1',
            !isInline && size !== 'lg' && 'text-xs sm:text-xs md:text-sm p-0'
          )}
        />
      )}
      {controls}
      {/* `flex` blockifies the button so it centres with the title text
         instead of baseline-aligning — see MemoFormControls. */}
      {showCloseButton && (
        <div className='shrink-0 flex'>
          <Tooltip title='Close'>
            <AriaButton
              aria-label='Close'
              onPress={onCancel}
              className={MEMO_ICON_BUTTON_CLASS}
            >
              <XClose className='size-4' />
            </AriaButton>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
